"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stringType = exports.stringFormat = void 0;
const actual_type_1 = require("./actual-type");
const caster_cache_1 = require("./caster-cache");
exports.stringFormat = {
    'date-iso8601': /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])T(0[0-9]|1[0-9]|2[0-3]):[0-5]\d:[0-5]\d\.\d{3}Z$/i,
    // https://github.com/epoberezkin/ajv/blob/d5edde43752e8c1bdb26074402452a41fe0742cb/lib/compile/formats.js#L46
    email: /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i,
    'gregorian-date': /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/,
    'gregorian-year': /^\d{4}$/,
    integer: /^-?\d+$/,
    'non-negative-integer': /^\d+$/,
    time12h: /^(0[1-9]|1[0-2]):[0-5]\d(am|pm)$/,
    time24h: /^([01][0-9]|2[0-3]):[0-5]\d$/,
    uuid: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
    pascalcase: /^[A-Z][a-z]+(?:[A-Z][a-z]+)*$/,
    camelcase: /^[a-z]+(?:[A-Z][a-z]+)*$/,
    kebabcase: /^[a-z][a-z0-9-]*$/,
    snakecase: /^[a-z][a-z0-9_]*$/
};
function stringType(declaration, validateOptions, circularCache = caster_cache_1.createCache()) {
    if (declaration === null || typeof declaration !== 'object') {
        throw new Error('First argument (declaration) must be an Object.');
    }
    const circularCaster = circularCache.get(declaration);
    if (circularCaster !== undefined) {
        return circularCaster;
    }
    const { type, required, strict, exclusiveMaximum, exclusiveMinimum, maximum, minimum, pattern, length, maxLength, minLength, blank, format, trim } = declaration;
    if (type !== 'String') {
        throw new Error('"type" property on declaration must be "String"');
    }
    if (required !== undefined && typeof required !== 'boolean') {
        throw new Error('"required" property on declaration must be undefined or a Boolean');
    }
    if (strict !== undefined && typeof strict !== 'boolean') {
        throw new Error('"strict" property on declaration must be undefined or a Boolean');
    }
    if (trim !== undefined && typeof trim !== 'boolean') {
        throw new Error('"trim" property on declaration must be undefined or a Boolean');
    }
    const options = Array.isArray(declaration.options) ? Array.from(declaration.options) : declaration.options;
    const formatPattern = typeof declaration.format === 'string' ? exports.stringFormat[declaration.format] : undefined;
    if (declaration.format !== undefined && formatPattern === undefined) {
        const formatNames = Object.keys(exports.stringFormat)
            .sort()
            .map((formatName) => `"${formatName}"`);
        const firstNames = formatNames.slice(0, -1);
        const lastName = formatNames.slice(-1);
        const formatsLiteral = firstNames.join(', ') + ' and ' + lastName;
        throw new Error(`"format" property on declaration must undefined or one of ${formatsLiteral}`);
    }
    const defaultCandidate = Reflect.get(declaration, 'default');
    if (defaultCandidate !== undefined && typeof defaultCandidate !== 'string') {
        throw new Error('"default" property on declaration must be undefined or a String');
    }
    let caster = (candidate) => ({ type, isValid: true, invalidations: [], candidate });
    if (options !== undefined) {
        const isNotArray = Array.isArray(declaration.options) === false;
        if (formatPattern !== undefined) {
            if (isNotArray ||
                options.find((opt) => typeof opt !== 'string' || formatPattern.test(opt) === false) !== undefined) {
                throw new Error('"options" property on declaration must undefined or an array of String of format "${declaration.format}"');
            }
        }
        else if (isNotArray || options.find((opt) => typeof opt !== 'string') !== undefined) {
            throw new Error('"options" property on declaration must undefined or an array of String');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (options.find((opt) => opt === candidate) === undefined) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'options', options, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (formatPattern !== undefined) {
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (formatPattern.test(candidate) === false) {
                return {
                    ...result,
                    invalidations: [
                        [[], 'String', `format-${declaration.format}`, declaration.format, candidate],
                        ...result.invalidations
                    ]
                };
            }
            return result;
        };
    }
    if (exclusiveMaximum !== undefined) {
        if (typeof exclusiveMaximum !== 'string') {
            throw new Error('"exclusiveMaximum" property on declaration must undefined or a String');
        }
        else if (formatPattern !== undefined && formatPattern.test(exclusiveMaximum) === false) {
            throw new Error(`"exclusiveMaximum" property on declaration must be undefined or a String of format "${format}"`);
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (exclusiveMaximum <= candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'exclusiveMaximum', exclusiveMaximum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (exclusiveMinimum !== undefined) {
        if (typeof exclusiveMinimum !== 'string') {
            throw new Error('"exclusiveMinimum" property on declaration must undefined or a String');
        }
        else if (formatPattern !== undefined && formatPattern.test(exclusiveMinimum) === false) {
            throw new Error(`"exclusiveMinimum" property on declaration must be undefined or a String of format "${format}"`);
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (exclusiveMinimum >= candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'exclusiveMinimum', exclusiveMinimum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (maximum !== undefined) {
        if (typeof maximum !== 'string') {
            throw new Error('"maximum" property on declaration must undefined or a String');
        }
        else if (formatPattern !== undefined && formatPattern.test(maximum) === false) {
            throw new Error(`"maximum" property on declaration must be undefined or a String of format "${format}"`);
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (maximum < candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'maximum', maximum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (minimum !== undefined) {
        if (typeof minimum !== 'string') {
            throw new Error('"minimum" property on declaration must undefined or a String');
        }
        else if (formatPattern !== undefined && formatPattern.test(minimum) === false) {
            throw new Error(`"minimum" property on declaration must be undefined or a String of format "${format}"`);
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (minimum > candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'minimum', minimum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (pattern !== undefined) {
        if (typeof pattern !== 'string') {
            throw new Error('"pattern" property on declaration must undefined or a String');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (RegExp(pattern).test(candidate) === false) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'pattern', pattern, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (length !== undefined) {
        if (typeof length !== 'number') {
            throw new Error('"length" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (length !== candidate.length) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'length', length, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (maxLength !== undefined) {
        if (typeof maxLength !== 'number') {
            throw new Error('"maxLength" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (maxLength < candidate.length) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'maxLength', maxLength, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (minLength !== undefined) {
        if (typeof minLength !== 'number') {
            throw new Error('"minLength" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (minLength > candidate.length) {
                return {
                    ...result,
                    invalidations: [[[], 'String', 'minLength', minLength, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (blank !== undefined) {
        if (typeof blank !== 'boolean') {
            throw new Error('"blank" property on declaration must undefined or a Boolean');
        }
        if (blank === false) {
            const casterRef = caster;
            caster = (candidate) => {
                const result = casterRef(candidate);
                const trimmed = candidate.trim();
                if (trimmed.length === 0) {
                    return {
                        ...result,
                        invalidations: [[[], 'String', 'blank', blank, candidate], ...result.invalidations]
                    };
                }
                return result;
            };
        }
    }
    const casterRef = (candidate) => {
        if ((candidate === undefined || candidate === null) && defaultCandidate !== undefined) {
            candidate = defaultCandidate;
        }
        if (candidate === undefined || candidate === null) {
            if (required === false) {
                return {
                    type,
                    isValid: true,
                    invalidations: [],
                    candidate
                };
            }
            else {
                return {
                    type,
                    isValid: false,
                    candidate,
                    invalidations: [[[], 'String', 'required', true, candidate]]
                };
            }
        }
        if (strict === false) {
            if (typeof candidate === 'number' ||
                typeof candidate === 'boolean' ||
                typeof candidate === 'bigint' ||
                candidate instanceof RegExp) {
                candidate = candidate.toString();
            }
            else if (candidate instanceof Date) {
                candidate = candidate.toISOString();
            }
        }
        const candidateType = actual_type_1.ctorName(candidate);
        if (candidateType !== type) {
            return {
                type,
                isValid: false,
                candidate,
                invalidations: [[[], 'String', 'type', type, candidateType]]
            };
        }
        if (trim === true) {
            candidate = candidate.trim();
        }
        const result = caster(candidate);
        result.isValid = result.invalidations.length === 0;
        return result;
    };
    return circularCache.set(declaration, casterRef);
}
exports.stringType = stringType;
